import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { OrderStatusViewModel } from '@app/shared/viewModels/order-status.viewModel';
import { OrderAdDetailStatus } from '@core/enums/order-ad-detail-status';
import { OrderStatus } from '@core/enums/order-status';
import { CancelOrderRequest } from '@core/models/cancel-order-request-model';
import { OrderAdDetail } from '@core/models/order-ad-detail.model';
import { OrderHeader } from '@core/models/order-header.model';
import { Order } from '@core/models/order.model';
import { Logger } from '@core/services/logger.service';
import { OrderEditRestrictionType } from '@order/enums/order-edit-restriction-type';
import { OrderEditRestriction } from '@order/models/order-edit-restriction';
import { DateTimeService } from '@shared/services/date-time.service';
import { StatusService } from '@shared/services/status.service';
import { BehaviorSubject } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class OrderHelperService {

	// TBD: move this into order status service
	isMaterialPanelReloadRequired$ = new BehaviorSubject<number | undefined>(undefined);

	private readonly COMPONENT_NAME = "OrderHelperService";

	constructor (
		private _dateTimeService: DateTimeService,
		private _statusService: StatusService,
		private _logger: Logger) { }

	doesDateArrayContainDate(dates: Date[], date: Date): boolean {
		let result = false;

		if (Array.isArray(dates) && dates.length > 0 && date instanceof Date) {
			result = dates.some((d) => {
				return d instanceof Date && d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
			})
		}

		return result;
	}

	// The method creates a Date from any string formatted as YYYYMMDD
	convertUrlStringDateToDate(urlStringDate: string): Date | undefined {
		if (urlStringDate && typeof urlStringDate === "string" && urlStringDate.length === 8) {
			const y = parseInt(urlStringDate.substring(0, 4));
			const m = parseInt(urlStringDate.substring(4, 6));
			const d = parseInt(urlStringDate.substring(6, 8));

			return new Date(y, m - 1, d);
		}
		return undefined;
	}

	getOrderEditRestriction(order: Order): OrderEditRestriction {

		const orderStatusVM = new OrderStatusViewModel(order.orderHeader);
		const dateTimeNow = new Date();

		if (orderStatusVM.cancelled) {
			return OrderEditRestriction.create(OrderEditRestrictionType.Cancelled);
		}
		else if (orderStatusVM.isNew || orderStatusVM.draft) {
			return OrderEditRestriction.create(OrderEditRestrictionType.Draft);
		}
		else if (this._statusService.doesAnyAdDetailHaveStatus(order, OrderAdDetailStatus.Invoiced)) {
			return OrderEditRestriction.create(OrderEditRestrictionType.Invoiced);
		}
		else if (orderStatusVM.confirmedAdCount > 0) {
			return OrderEditRestriction.create(OrderEditRestrictionType.Confirmed);
		}

		// The BE should be sending orderAdDetail[x].materialDeadline and bookingDeadline

		else if (order && order.orderAdDetails && order.orderAdDetails.some((orderLine: OrderAdDetail) => {
			if (orderLine.materialDeadline) {
				let materialDeadline = orderLine.materialDeadline;
				if (materialDeadline.slice(-1) !== "Z" || materialDeadline.slice(-1) !== "z") {
					materialDeadline += "Z";
				}
				const materialDeadlineDateTime = new Date(materialDeadline);
				if (materialDeadlineDateTime >= dateTimeNow) {
					return true;
				}
			}
			return false;
		})) {
			return OrderEditRestriction.create(OrderEditRestrictionType.PastMaterialDeadline);
		}

		//TODO: complete the BL cases
		//	else if (PastBooking) {
		//	}
		else {
			return OrderEditRestriction.create(OrderEditRestrictionType.Reserved);
		}
	}

	getOrderLinesEditRestriction(orderLines: OrderAdDetail[]): OrderEditRestriction {
		if (orderLines && orderLines.length > 0) {
			if (orderLines.some((orderLine: OrderAdDetail) => this._statusService.doesAdDetailHaveStatus(orderLine, OrderAdDetailStatus.Cancelled))) {
				return OrderEditRestriction.create(OrderEditRestrictionType.Cancelled);
			}

			if (orderLines.some((orderLine: OrderAdDetail) => this._statusService.doesAdDetailHaveStatus(orderLine, OrderAdDetailStatus.Invoiced))) {
				return OrderEditRestriction.create(OrderEditRestrictionType.Invoiced);
			}

			const dateTimeNow = new Date();

			if (orderLines.some((orderLine: OrderAdDetail) => this.isUtcDatePast(orderLine.materialDeadline, dateTimeNow))) {
				return OrderEditRestriction.create(OrderEditRestrictionType.PastMaterialDeadline);
			}

			if (orderLines.some((orderLine: OrderAdDetail) => this.isUtcDatePast(orderLine.bookingDeadline, dateTimeNow))) {
				return OrderEditRestriction.create(OrderEditRestrictionType.PastBookingDeadline);
			}

			if (orderLines.some((orderLine: OrderAdDetail) => this._statusService.doesAdDetailHaveStatus(orderLine, OrderAdDetailStatus.Confirmed))) {
				return OrderEditRestriction.create(OrderEditRestrictionType.Confirmed);
			}

			return OrderEditRestriction.create(OrderEditRestrictionType.Draft);
		}

		return OrderEditRestriction.create(OrderEditRestrictionType.None);
	}

	getSomeOrderLinesPastBookingDate(orderLines: OrderAdDetail[]): boolean {
		if (orderLines && orderLines.length > 0) {
			const dateTimeNow = new Date();
			if (orderLines.some((orderLine: OrderAdDetail) => this.isUtcDatePast(orderLine.bookingDeadline, dateTimeNow))) {
				return true;
			}
		}

		return false;
	}

	isUtcDatePast(utcDateString: string | undefined, referenceUtcDateTime: Date): boolean {
		if (utcDateString) {

			// Currently backend sends date/time strings in UTC format without the trailing Z, so make sure to append it if required.
			// Otherwise the Date() constructor creates wrong date.
			let normalizedUtcDateTimeString = utcDateString;
			if (normalizedUtcDateTimeString.slice(-1) !== "Z" && normalizedUtcDateTimeString.slice(-1) !== "z") {
				normalizedUtcDateTimeString += "Z";
			}

			const normalizedLocalDateTime = new Date(normalizedUtcDateTimeString);

			if (referenceUtcDateTime >= normalizedLocalDateTime) {
				return true;
			}
		}
		return false;
	}

	getNextMaterialSequenceCode(sequenceCode: string): string {
		if (!sequenceCode) {
			return "001";
		}

		let intVal = parseInt(sequenceCode, 10) + 1;

		return intVal.toString().padStart(3, '0');
	}

	resetOrderCosting(order: Order): void {
		if (order) {
			order.priceGross = undefined;
			order.taxPercentage = undefined;
			order.currencyCode = undefined;
			order.priceGross = undefined;
			order.discountAmount = undefined;
			order.priceNetExComm = undefined;
			//			order.priceCommissionTotal = undefined;
			//			order.taxAmount = undefined;
			order.taxPercentage = undefined;

			if (order.orderHeader) {
				order.orderHeader.priceNetExTax = undefined;
				order.orderHeader.priceNetIncTax = undefined;

				if (!order.orderAdDetails || order.orderAdDetails.length === 0) {
					order.orderHeader.startDate = undefined;
					order.orderHeader.endDate = undefined;
					order.orderHeader.nextDate = undefined;
					order.orderHeader.adCount = 0;
					order.orderHeader.adCountRemaining = 0;
				}
			}
		}
	}

	updateOrderCostingWithCalcResult(orderToUpdate: Order, calculatedOrder: Order): void {
		if (orderToUpdate && calculatedOrder) {
			orderToUpdate.priceGross = calculatedOrder.priceGross;
			orderToUpdate.taxPercentage = calculatedOrder.taxPercentage;
			orderToUpdate.discountAmount = calculatedOrder.discountAmount;
			orderToUpdate.currencyCode = calculatedOrder.currencyCode;

			orderToUpdate.orderHeader.priceNetExTax = calculatedOrder.orderHeader.priceNetExTax;
			orderToUpdate.orderHeader.priceNetIncTax = calculatedOrder.orderHeader.priceNetIncTax;
			orderToUpdate.orderHeader.priceNetExComm = calculatedOrder.orderHeader.priceNetExComm;
			orderToUpdate.orderHeader.commissionAmount = calculatedOrder.orderHeader.commissionAmount;
			orderToUpdate.orderHeader.taxAmount = calculatedOrder.orderHeader.taxAmount;

			orderToUpdate.orderHeader.statusCounts = calculatedOrder.orderHeader.statusCounts;
		}
	}

	updateOrderLinesWithCalcResult(orderLinesToUpdate: OrderAdDetail[], calculatedOrderLines: OrderAdDetail[]): void {

		const matchCandidates = [...orderLinesToUpdate];

		for (const calcOrderAdDetail of calculatedOrderLines) {

			let orderAdDetail: OrderAdDetail | undefined = undefined;

			// TODO a bug in BE means orderAdDetails always have a newly-generated id (on every call to calculate!)
			// So for now don't use existing ids for matching.

			let findIndex = -1;
			// if (calcOrderAdDetail.id !== undefined) {
			// 	// find corresponding orderAdDetail by id
			// 	findIndex = matchCandidates.findIndex((detail: OrderAdDetail) => detail.id === calcOrderAdDetail.id);
			// }
			// else {
			// find remaingin match candidate orderAdDetail by [package, placement, date, moduleCode|[columns, depth]]
			findIndex = matchCandidates.findIndex(detail =>
				detail.packageId === calcOrderAdDetail.packageId &&
				detail.placementId === calcOrderAdDetail.placementId &&
				detail.issueDate === calcOrderAdDetail.issueDate &&
				(detail.moduleCode
					? detail.moduleCode === calcOrderAdDetail.moduleCode
					: detail.columns === calcOrderAdDetail.columns && detail.depth === calcOrderAdDetail.depth)
			);
			// }

			if (findIndex >= 0) {
				orderAdDetail = matchCandidates[findIndex];
				matchCandidates.splice(findIndex, 1);
				// update
				orderAdDetail.columns = calcOrderAdDetail.columns;
				orderAdDetail.depth = calcOrderAdDetail.depth;
				// orderAdDetail.materialId = calcOrderAdDetail.materialId;
				orderAdDetail.materialDeadline = calcOrderAdDetail.materialDeadline;
				orderAdDetail.bookingDeadline = calcOrderAdDetail.bookingDeadline;
				orderAdDetail.commissionAmount = calcOrderAdDetail.commissionAmount;
				orderAdDetail.discountAmount = calcOrderAdDetail.discountAmount;
				orderAdDetail.priceGross = calcOrderAdDetail.priceGross;
				orderAdDetail.width = calcOrderAdDetail.width;
				orderAdDetail.statusFlags = calcOrderAdDetail.statusFlags;
				// orderAdDetail.orderId is not updated as the Calc response does not contain any useful data
			}
			// the matchCandidates is only a subset of all the order lines, so it is perfectly fine not to find calculated order line among the current lines
			//	else {
			//		// report bug
			//		this._logger.error(this.COMPONENT_NAME, `updateOrderLinesWithCalcResult(): Cannot find the orderAdDetail by Id or package-placement-issue combination. Possible bug.`);
			//		console.log("details: calcOrderAdDetail=", calcOrderAdDetail);
			//	}
		}
	}

	//private findOrderLine(orderLines: OrderAdDetail[], orderLineToFind: OrderAdDetail): OrderAdDetail | undefined {

	//	const res = orderLines.find((line: OrderAdDetail) => {
	//		if (orderLineToFind.id && orderLineToFind.id === line.id) {
	//			return true; // found by id
	//		}
	//		else {
	//			// note: currently the product Id is not in the BE interfaces
	//			return (line.issueDate === orderLineToFind.issueDate) &&
	//				(line.packageId === orderLineToFind.packageId) &&
	//				(line.placementId === orderLineToFind.placementId)
	//		}
	//	});

	//	return res;
	//}

	areMaterilalIdsInUse(orderAdDetails: OrderAdDetail[]): boolean {
		return orderAdDetails.some((orderAdDetail: OrderAdDetail) => orderAdDetail.materialId);
	}

	getMaxUsedMaterialIdBySize(orderAdDetails: OrderAdDetail[], masterSizeOrderAdDetail: OrderAdDetail): string | undefined {

		const sameSizeLinesWithMaterial = orderAdDetails.filter((orderAdDetail: OrderAdDetail) => {
			if (!orderAdDetail.materialId) {
				return false;
			}

			// same size even if different module code should mean same material; so the search for existing material id is performed using actual size: width(MM) x depth(MM)
			return orderAdDetail.width === masterSizeOrderAdDetail.width && orderAdDetail.depth === masterSizeOrderAdDetail.depth;
		});

		if (sameSizeLinesWithMaterial.length > 0) {
			const lineWithMaxUsedMaterialId = sameSizeLinesWithMaterial.reduce((prev: OrderAdDetail, current: OrderAdDetail) => (prev.materialId > current.materialId) ? prev : current);

			if (lineWithMaxUsedMaterialId) {
				return lineWithMaxUsedMaterialId.materialId;
			}
		}

		return undefined;
	}

	updateOrderStartEndDates(order: Order): void {
		// TODO the API should set these fields to move logic into the BE
		order.orderHeader.adCount = 0;
		order.orderHeader.adCountRemaining = 0;
		order.orderHeader.startDate = undefined;
		order.orderHeader.endDate = undefined;
		order.orderHeader.nextDate = undefined;
		const todayString = formatDate(new Date(), "yyyy-MM-dd", "en-US");
		for (const adDetail of order.orderAdDetails) {
			if (Array.isArray(adDetail.statusFlags) && !adDetail.statusFlags.includes(OrderStatus.Cancelled)) {
				order.orderHeader.adCount += 1;
				// Date comparison will be wrong when user and server are in different timezones
				// should use published status (needs to be added to API).
				order.orderHeader.adCountRemaining += (adDetail.issueDate >= todayString ? 1 : 0);
				if (!order.orderHeader.startDate || adDetail.issueDate < order.orderHeader.startDate) {
					order.orderHeader.startDate = adDetail.issueDate;
				}
				if (!order.orderHeader.endDate || adDetail.issueDate > order.orderHeader.endDate) {
					order.orderHeader.endDate = adDetail.issueDate;
				}
				if (adDetail.issueDate >= todayString && (!order.orderHeader.nextDate || adDetail.issueDate < order.orderHeader.nextDate)) {
					order.orderHeader.nextDate = adDetail.issueDate;
				}
			}
		}
	}

	changeMaterialPanelReloadStatus(value: any) {
		this.isMaterialPanelReloadRequired$.next(value);
	}

	createCancelOrderRequest(order: Order): CancelOrderRequest {
		const request = new CancelOrderRequest();
		request.id = order.orderHeader.id;
		request.advertiserId = order.orderHeader.advertiserId;
		request.otherPayerId = order.orderHeader.otherPayerId;
		return request;
	}

	getIsDirectOrder(order: Order): boolean {
		if (order.orderHeader && !order.orderHeader.otherPayerId) {
			return true;
		}
		return false;
	}

	composeFullMaterialId(parentOrderId: string | undefined, materialId: string | undefined): string {

		if (!materialId) {
			return "";
		}

		if (parentOrderId && materialId) {
			return `${parentOrderId}_${materialId}`;
		}

		return materialId;
	}

	changeMaterial(orderLines: OrderAdDetail[], selectedOrderLines: OrderAdDetail[]): void {
		// CASS-101, corrected by CASS-452 and CAI-1485
		// : Material issue association
		// 1. The non - selected order lines that have no material sequence code are grouped by size.
		// 2. Each group of non - selected, no - sequence, same - size order lines are given the next unused sequence code.
		// 3. The selected order lines are grouped by size.
		// 4. Each group of selected, same - size order lines are given the next unused sequence code.
		// 5. The result is that
		//     a) all order lines have a sequence code,
		//     b) no sequence code is shared between order lines of different sizes, and
		//     c) the selected order lines now have a new sequence code.

		let lastUsedMaterialId = "";
		lastUsedMaterialId = orderLines.reduce((max, obj) => (obj.materialId > max ? obj.materialId : max), lastUsedMaterialId);

		// 1. The non - selected order lines that have no material sequence code are grouped by parent order id and size.

		const nonSelectedOrderLinesWithoutMaterialIds = orderLines.filter((orderLine: OrderAdDetail) => !orderLine.materialId && !selectedOrderLines.some((selectedOrderLine: OrderAdDetail) => selectedOrderLine === orderLine));
		const sameSizeNonSelectedOrderLinesMap = this.groupOrderAdDetailsByParentOrderIdAndSize(nonSelectedOrderLinesWithoutMaterialIds);
		const sameSizeNonSelectedOrderLines = sameSizeNonSelectedOrderLinesMap.values();

		// const nonSelectedOrderLines2 = this.order.orderAdDetails.filter((orderLine: OrderAdDetail) => !this.selection.selected.some((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel === orderLine));

		//const sameSizeNonSelectedOrderLinesMap2 = this.groupOrderAdDetailsBySize(nonSelectedOrderLines);


		//const sameSizeNonSelectedOrderLines2 = sameSizeNonSelectedOrderLinesMap.values();

		const dateTimeNow = new Date();

		// 2. Each group of non - selected, no - sequence, same parent order id and same size order lines are given the next unused sequence code.
		for (const sameSizeLines of sameSizeNonSelectedOrderLines) {
			// to each group of the orderAdDetails with the same parent order id and size, assign new material sequence code
			if (sameSizeLines && sameSizeLines.length > 0) {
				const currentSequenceCode = this.getNextMaterialSequenceCode(lastUsedMaterialId);
				sameSizeLines.forEach((orderAdDetail: OrderAdDetail) => {
					// update the materialId only for order lines which are not past material deadline
					if (!orderAdDetail.materialDeadline || !this.isUtcDatePast(orderAdDetail.materialDeadline, dateTimeNow)) {
						orderAdDetail.materialId = currentSequenceCode;
					}
				});
				lastUsedMaterialId = currentSequenceCode;
			}
		}

		// 3. The selected order lines are grouped by parent order id and size

		const sameSizeSelectedOrderLinesMap = this.groupOrderAdDetailsByParentOrderIdAndSize(selectedOrderLines);
		const sameSizeSelectedOrderLines = sameSizeSelectedOrderLinesMap.values();


		// const selectedOrderLines = this.selection.selected.map((orderLineVM: OrderLineDetailViewModel) => orderLineVM.orderLineModel);
		// const sameSizeSelectedOrderLinesMap = this.groupOrderAdDetailsBySize(selectedOrderLines);
		//const sameSizeSelectedOrderLines = sameSizeSelectedOrderLinesMap.values();

		// 4. Each group of selected, same parent order id and same size order lines are given the next unused sequence code
		for (const sameSizeLines of sameSizeSelectedOrderLines) {
			// to each group of the orderAdDetails with the same size, assign new material sequence code
			if (sameSizeLines && sameSizeLines.length > 0) {
				const currentSequenceCode = this.getNextMaterialSequenceCode(lastUsedMaterialId);
				sameSizeLines.forEach((orderAdDetail: OrderAdDetail) => {
					// update the materialId only for order lines which are not past material deadline
					if (!orderAdDetail.materialDeadline || !this.isUtcDatePast(orderAdDetail.materialDeadline, dateTimeNow)) {
						orderAdDetail.materialId = currentSequenceCode;
					}
				});
				lastUsedMaterialId = currentSequenceCode;
			}
		}
	}

	private groupOrderAdDetailsByParentOrderIdAndSize(orderAdDetails: OrderAdDetail[]): Map<string, OrderAdDetail[]> {
		// Map is used here instead of a Record because the Map keeps the order of the added items, so first order line will have material Id = "001"

		return orderAdDetails.reduce((acc, obj) => {
			const key = `${obj.orderId || "z"}_${obj.depth}x${obj.width}`;


			// const sizeName = obj.depth + "x" + obj.width;

			let arr = acc.get(key);
			if (!arr) {
				arr = [];
				acc.set(key, arr);
			}

			arr.push(obj);

			return acc;

		}, new Map<string, OrderAdDetail[]>);
	}
}
